import { useMemo } from 'react';
import { rage, useObservable } from '~/rage';
import { VehicleTuningCategory } from '~/shared';
import { VEHICLE_TUNING_SHOP_CATEGORIES } from '~/layouts/tuningStudio/tuningGroups.model';

const useTuningCategories = () => {
	const shopCategories = VEHICLE_TUNING_SHOP_CATEGORIES;
	const shopItems = useObservable(rage.vehicleTuningShop.getShopItems());
	const wheelSettingsRanges = useObservable(
		rage.vehicleTuningShop.getWheelsSettingsRanges(),
	);
	const ownedWheelSettings = useObservable(
		rage.vehicleTuningShop.getOwnedWheelsSettings(),
	);

	const isUsingStockWheels = useMemo(
		() =>
			shopItems.some(
				(e) =>
					e.categoryId === VehicleTuningCategory.RIMS &&
					e.isInstalled &&
					e.id.startsWith('stock_'),
			),
		[shopItems],
	);

	const filteredShopCategories = useMemo(() => {
		if (shopCategories.length === 0) {
			return [];
		}

		return shopCategories.filter((category) => {
			if (category.id === VehicleTuningCategory.WHEEL_SETTINGS) {
				if (!wheelSettingsRanges || !ownedWheelSettings || isUsingStockWheels) {
					return false;
				}
			} else {
				const categoryItems = shopItems.filter(
					(e) => e.categoryId === category.id,
				);
				const nonStockItems = categoryItems.filter(
					(e) => !e.id.startsWith('stock_'),
				);
				if (nonStockItems.length === 0) {
					return false;
				}
			}

			return true;
		});
	}, [
		shopCategories,
		wheelSettingsRanges,
		ownedWheelSettings,
		isUsingStockWheels,
		shopItems,
	]);

	const ROOT_CATEGORY_ORDER: VehicleTuningCategory[] = [
		VehicleTuningCategory.RIMS,
		VehicleTuningCategory.WHEEL_SETTINGS,
	];

	const nonExteriorCategories = useMemo(() => {
		const byId = new Map(filteredShopCategories.map((c) => [c.id, c]));
		return ROOT_CATEGORY_ORDER.flatMap((id) => {
			const cat = byId.get(id);
			return cat ? [cat] : [];
		});
	}, [filteredShopCategories]);

	const exteriorCategories = useMemo(() => {
		const rootSet = new Set(ROOT_CATEGORY_ORDER);
		const list = filteredShopCategories.filter((c) => !rootSet.has(c.id));
		// Наборы всегда первыми в списке внешнего тюнинга.
		return list.sort((a, b) => {
			if (a.id === VehicleTuningCategory.KITS) return -1;
			if (b.id === VehicleTuningCategory.KITS) return 1;
			return 0;
		});
	}, [filteredShopCategories]);

	return {
		shopCategories,
		shopItems,
		filteredShopCategories,
		exteriorCategories,
		nonExteriorCategories,
		isUsingStockWheels,
		wheelSettingsRanges,
		ownedWheelSettings,
	};
};

export { useTuningCategories };
